/* eslint-disable no-param-reassign */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ReviewOrganizationResponse } from 'src/openapi';
import {
  acceptJobReferralVerification,
  createAdminChatTemplate,
  deleteAdminChatTemplate,
  getActivityLogsForAdmin,
  getActivityTypeCountsForAdmin,
  getAdminFeedbacks,
  getAuditLogsForAdmin,
  getAuditLogEntityTypesForAdmin,
  getAdminMailTemplateById,
  getAllAdminChatTemplates,
  getAllBadge,
  getAllCoupons,
  getAllLevels,
  getAllPlansAdmin,
  getBadgeType,
  getChatLogsAdmin,
  getCollegeData,
  getCompanyData,
  getCouponById,
  getDegreeData,
  getEmailTemplates,
  getExamData,
  getJobRoleData,
  getLevelById,
  getMailGroupsLogs,
  getMailGroupUsersByID,
  getMatchingProfile,
  getMatchingResumes,
  getMocktestAdmin,
  getMocktestById,
  getOrdersAdmin,
  getOrdersUserAddress,
  getOrgActivityLogs,
  getOrgsDetailsById,
  getPotentialRecruitersBulk,
  getQuestionReviewTemplates,
  getRelatedMocktest,
  getReviewJobById,
  getAdminPendingCounts,
  getReviewJobs,
  getReviewOrgs,
  getReviewQuestions,
  getSkillsData,
  getSpecializationData,
  createStoreItemsAdmin,
  getStoreItemsAdmin,
  getStoreItemTypeCounts,
  updateStoreItemsAdmin,
  getUserMailLogs,
  getUsersList,
  getWhatsappTemplates,
  getGemsActivityForAdmin,
  sendEmail,
  sendEmployerEmail,
  sendFollowUpEmail,
  sendWhatsappNotification,
  updateAdminChatTemplate,
  getReferralActivityLogsAdmin,
  getReferralSummaryAdmin,
  getSkillStatisticsAdmin,
  getSkillTypeLists,
  recomputeSkillQualityAdmin,
  updateSkillAllowImagesAdmin,
  updateSkillCategoriesAdmin,
  updateSkillHiddenAdmin,
} from 'src/redux/actions/admin';
import { LS_KEY_SKILL_HEIRARCHY_BREADCRUMB } from 'src/constants/localStorageKeys';
import type { BreadcrumbItem, ReviewState } from '../types/admin';

const organisations: ReviewOrganizationResponse = {
  org_list: [],
  page: 1,
  page_no: 10,
  total_pages: 1,
};
type RemoveReviewedQuestionsPayload = {
  questionIds: string[];
};

// Persisted breadcrumb survives page refresh in the same tab. sessionStorage
// (not localStorage) so two admin tabs don't fight over each other's nav.
const loadBreadcrumb = (): BreadcrumbItem[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(
      LS_KEY_SKILL_HEIRARCHY_BREADCRUMB,
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as BreadcrumbItem[]) : [];
  } catch {
    return [];
  }
};

const persistBreadcrumb = (value: BreadcrumbItem[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      LS_KEY_SKILL_HEIRARCHY_BREADCRUMB,
      JSON.stringify(value),
    );
  } catch {
    // ignore quota / disabled storage
  }
};

// The count preview's suppression fields aren't on the generated client until the
// spec is re-pulled post-deploy, and are absent altogether on audiences too large
// for the backend to compute them — so undefined means "unknown", never "zero".
type AudiencePreviewPayload = {
  email_count?: number;
  duplicate_count?: number;
  duplicate_days?: number | null;
  duplicate_all_time?: boolean;
  suppressed_count?: number;
  suppressed_breakdown?: Record<string, number>;
  forceable_count?: number;
  applied_count?: number;
};

const applyAudiencePreview = (
  state: ReviewState,
  payload: AudiencePreviewPayload,
) => {
  // Async sends return {queued:true} with no count — keep the reviewed count.
  state.emailCount = payload.email_count ?? state.emailCount;
  // How many of this audience already got this subject/link recently, and what
  // the rest of the gate will do to them. The backend skips all of it unless the
  // send opts in — and only the duplicate/cap/fatigue part can be opted out of.
  state.duplicateCount = payload.duplicate_count ?? undefined;
  state.duplicateDays = payload.duplicate_days ?? undefined;
  state.duplicateAllTime = payload.duplicate_all_time ?? undefined;
  state.suppressedCount = payload.suppressed_count ?? undefined;
  state.suppressedBreakdown = payload.suppressed_breakdown ?? undefined;
  state.forceableCount = payload.forceable_count ?? undefined;
  state.appliedCount = payload.applied_count ?? undefined;
};

const initialState: ReviewState = {
  organisations,
  orgDetails: undefined,
  isFetchingOrg: false,
  activityLogs: [],
  mailLogs: [],
  reviewJobs: null,
  isFetchingReviewJobs: false,
  reviewJobById: null,
  matchingResumes: null,
  matchingUsers: null,
  emailTemplates: null,
  templateDetails: null,
  whatsappTemplates: [],
  whatsappDryRun: null,
  sendingWhatsapp: false,
  userList: null,
  isFetchingUserList: false,
  adminPendingCounts: null,
  adminChatTemplates: null,
  mailGroupLogs: null,
  isFetchingMailGroupLogs: false,
  mailGroupUserList: null,
  mocktests: null,
  mocktestById: null,
  emailCount: undefined,
  duplicateCount: undefined,
  duplicateDays: undefined,
  duplicateAllTime: undefined,
  suppressedBreakdown: undefined,
  suppressedCount: undefined,
  forceableCount: undefined,
  appliedCount: undefined,
  isSendingBulkEmail: false,
  isSendingFollowUp: false,
  reviewQuestions: null,
  isFetchingReviewQuestions: false,
  reviewTemplates: null,
  allLevels: null,
  levelById: null,
  allBadges: null,
  allCoupons: null,
  badgeTypes: null,
  feedbacks: null,
  storeItems: null,
  storeItemTypeCounts: null,
  storeItemPlantype: null,
  adminOrders: null,
  orderById: null,
  potentialRecruiters: null,
  collegeData: null,
  companyData: null,
  examData: null,
  skillsData: null,
  jobRoleData: null,
  degreeData: null,
  specializationData: null,
  couponById: null,
  chatLogs: null,
  adminActivity: null,
  adminAuditLogs: null,
  auditLogEntityTypes: [],
  adminActivityTypes: [],
  adminGemsActivity: null,
  isFetchingGemsActivity: false,
  referralSummary: null,
  skillStatistics: null,
  skillList: null,
  adminSkillList: null,
  skillHeirarchyBreadcrumb: loadBreadcrumb(),
  referralActivityLogs: null,
};

const adminSlice = createSlice({
  name: 'adminSlice',
  initialState,
  reducers: {
    setBreadcrumb: (state, action) => {
      state.skillHeirarchyBreadcrumb = action.payload;
      persistBreadcrumb(state.skillHeirarchyBreadcrumb);
    },

    addBreadcrumb: (state, action) => {
      const exists = state.skillHeirarchyBreadcrumb.find(
        (b) => b.id === action.payload.id,
      );

      if (!exists) {
        state.skillHeirarchyBreadcrumb.push(action.payload);
        persistBreadcrumb(state.skillHeirarchyBreadcrumb);
      }
    },

    resetBreadcrumb: (state) => {
      state.skillHeirarchyBreadcrumb = [];
      persistBreadcrumb(state.skillHeirarchyBreadcrumb);
    },
    trimBreadcrumb: (state, action) => {
      const id = action.payload;

      const index = state.skillHeirarchyBreadcrumb.findIndex(
        (b) => b.id === id,
      );

      if (index !== -1) {
        state.skillHeirarchyBreadcrumb = state.skillHeirarchyBreadcrumb.slice(
          0,
          index + 1,
        );
        persistBreadcrumb(state.skillHeirarchyBreadcrumb);
      }
    },

    resetReviewJobById: (state) => {
      state.reviewJobById = null;
    },
    resetMocktestById: (state) => {
      state.mocktestById = null;
    },
    updateAppliedStatus: (state, action: PayloadAction<{ userId: string }>) => {
      if (!state.matchingUsers?.matching_profiles) return;

      const user = state.matchingUsers.matching_profiles.find(
        (p) => p.user_info?.id === action.payload.userId,
      );

      if (user) {
        user.has_applied = true;
      }
    },

    // refresh review questions page
    removeReviewedQuestionsFromList: (
      state,
      action: PayloadAction<RemoveReviewedQuestionsPayload>,
    ) => {
      const ids = action.payload.questionIds;

      if (state.reviewQuestions?.data) {
        state.reviewQuestions.data = state.reviewQuestions.data.filter(
          (q) => q.id && !ids.includes(q.id),
        );
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getReviewOrgs.pending, (state) => {
        state.isFetchingOrg = true;
      })
      .addCase(getOrgsDetailsById.pending, (state) => {
        state.isFetchingOrg = true;
      })
      .addCase(getReviewOrgs.fulfilled, (state, action) => {
        state.isFetchingOrg = false;
        state.organisations = action.payload;
      })
      .addCase(getReviewJobs.pending, (state) => {
        state.isFetchingReviewJobs = true;
      })
      .addCase(getReviewJobs.fulfilled, (state, action) => {
        state.reviewJobs = action.payload;
        state.isFetchingReviewJobs = false;
      })
      .addCase(getReviewJobs.rejected, (state, action) => {
        // A cancelled request means a newer one is already in flight —
        // leave its pending/fulfilled state alone.
        if ((action.payload as { cancelled?: boolean })?.cancelled) return;
        state.isFetchingReviewJobs = false;
      })
      .addCase(getUsersList.pending, (state) => {
        state.isFetchingUserList = true;
      })
      .addCase(getUsersList.fulfilled, (state, action) => {
        state.userList = action.payload;
        state.isFetchingUserList = false;
      })
      .addCase(getUsersList.rejected, (state) => {
        state.isFetchingUserList = false;
      })
      .addCase(getAdminPendingCounts.fulfilled, (state, action) => {
        state.adminPendingCounts = action.payload;
      })
      .addCase(getPotentialRecruitersBulk.fulfilled, (state, action) => {
        state.potentialRecruiters = action.payload;
      })
      .addCase(getMailGroupsLogs.pending, (state) => {
        state.isFetchingMailGroupLogs = true;
      })
      .addCase(getMailGroupsLogs.fulfilled, (state, action) => {
        state.mailGroupLogs = action.payload;
        state.isFetchingMailGroupLogs = false;
      })
      .addCase(getMailGroupsLogs.rejected, (state) => {
        state.isFetchingMailGroupLogs = false;
      })
      .addCase(getMailGroupUsersByID.fulfilled, (state, action) => {
        state.mailGroupUserList = action.payload;
      })
      .addCase(sendFollowUpEmail.pending, (state) => {
        state.isSendingFollowUp = true;
      })
      .addCase(sendFollowUpEmail.fulfilled, (state) => {
        state.isSendingFollowUp = false;
      })
      .addCase(sendFollowUpEmail.rejected, (state) => {
        state.isSendingFollowUp = false;
      })
      .addCase(getReviewJobById.fulfilled, (state, action) => {
        state.reviewJobById = action.payload?.jobs?.[0] ?? null;
      })
      .addCase(getRelatedMocktest.fulfilled, (state, action) => {
        state.relatedMocktests = action.payload;
      })
      .addCase(getAllPlansAdmin.fulfilled, (state, action) => {
        state.storeItemPlantype = action.payload;
      })
      .addCase(getOrdersAdmin.fulfilled, (state, action) => {
        state.adminOrders = action.payload;
      })
      .addCase(getOrdersUserAddress.fulfilled, (state, action) => {
        state.orderById = action.payload;
      })
      .addCase(getEmailTemplates.fulfilled, (state, action) => {
        state.emailTemplates = action.payload.templates;
      })
      .addCase(getMocktestById.fulfilled, (state, action) => {
        state.mocktestById = action.payload;
      })
      .addCase(getStoreItemsAdmin.fulfilled, (state, action) => {
        state.storeItems = action.payload;
      })
      // Patch the one changed/created row in place rather than refetching —
      // a refetch here would need the caller's current type/search filters,
      // which this slice doesn't have, and would otherwise overwrite
      // whichever page's filtered list happens to be loaded with an
      // unfiltered one (see CreateEditItem, opened as a modal on top of a
      // type-filtered admin list page).
      .addCase(updateStoreItemsAdmin.fulfilled, (state, action) => {
        const updatedItem = action.payload.updated_item;
        if (!state.storeItems?.items || !updatedItem) return;
        state.storeItems.items = state.storeItems.items.map((item) =>
          item.id === updatedItem.id ? updatedItem : item,
        );
      })
      .addCase(createStoreItemsAdmin.fulfilled, (state, action) => {
        const createdItem = action.payload.created_item;
        if (!state.storeItems?.items || !createdItem) return;
        state.storeItems.items = [createdItem, ...state.storeItems.items];
      })
      .addCase(getStoreItemTypeCounts.fulfilled, (state, action) => {
        state.storeItemTypeCounts = action.payload;
      })
      .addCase(sendEmail.pending, (state) => {
        state.emailCount = 0;
        state.isSendingBulkEmail = true;
      })
      .addCase(sendEmail.rejected, (state) => {
        state.emailCount = 0;
        state.isSendingBulkEmail = false;
      })
      .addCase(sendEmail.fulfilled, (state, action) => {
        applyAudiencePreview(state, action.payload);
        state.isSendingBulkEmail = false;
      })
      .addCase(sendEmployerEmail.pending, (state) => {
        state.emailCount = 0;
        state.isSendingBulkEmail = true;
      })
      .addCase(sendEmployerEmail.rejected, (state) => {
        state.emailCount = 0;
        state.isSendingBulkEmail = false;
      })
      .addCase(sendEmployerEmail.fulfilled, (state, action) => {
        applyAudiencePreview(state, action.payload);
        state.isSendingBulkEmail = false;
      })
      .addCase(getReviewQuestions.pending, (state) => {
        state.reviewQuestions = null;
        state.isFetchingReviewQuestions = true;
      })
      .addCase(getReviewQuestions.fulfilled, (state, action) => {
        state.reviewQuestions = action.payload;
        state.isFetchingReviewQuestions = false;
      })
      .addCase(getReviewQuestions.rejected, (state) => {
        state.reviewQuestions = null;
        state.isFetchingReviewQuestions = false;
      })
      .addCase(getQuestionReviewTemplates.fulfilled, (state, action) => {
        state.reviewTemplates = action.payload;
      })
      .addCase(getCollegeData.fulfilled, (state, action) => {
        if (action.meta.arg?.skipStore) return;
        state.collegeData = action.payload.data;
      })
      .addCase(getCompanyData.fulfilled, (state, action) => {
        if (action.meta.arg?.skipStore) return;
        state.companyData = action.payload.data;
      })
      .addCase(getExamData.fulfilled, (state, action) => {
        if (action.meta.arg?.skipStore) return;
        state.examData = action.payload.data;
      })
      .addCase(getJobRoleData.fulfilled, (state, action) => {
        if (action.meta.arg?.skipStore) return;
        state.jobRoleData = action.payload.data;
      })
      .addCase(getSkillsData.fulfilled, (state, action) => {
        if (action.meta.arg?.skipStore) return;
        state.skillsData = action.payload.data;
      })
      .addCase(getDegreeData.fulfilled, (state, action) => {
        if (action.meta.arg?.skipStore) return;
        state.degreeData = action.payload.data;
      })
      .addCase(getSpecializationData.fulfilled, (state, action) => {
        if (action.meta.arg?.skipStore) return;
        state.specializationData = action.payload.data;
      })
      .addCase(getChatLogsAdmin.fulfilled, (state, action) => {
        state.chatLogs = action.payload;
      })
      .addCase(getActivityLogsForAdmin.fulfilled, (state, action) => {
        state.adminActivity = action.payload;
      })
      .addCase(getAuditLogsForAdmin.fulfilled, (state, action) => {
        state.adminAuditLogs = action.payload;
      })
      .addCase(getAuditLogEntityTypesForAdmin.fulfilled, (state, action) => {
        state.auditLogEntityTypes = action.payload;
      })
      .addCase(getActivityTypeCountsForAdmin.fulfilled, (state, action) => {
        state.adminActivityTypes = action.payload;
      })
      .addCase(getGemsActivityForAdmin.pending, (state) => {
        state.isFetchingGemsActivity = true;
      })
      .addCase(getGemsActivityForAdmin.fulfilled, (state, action) => {
        state.adminGemsActivity = action.payload;
        state.isFetchingGemsActivity = false;
      })
      .addCase(getGemsActivityForAdmin.rejected, (state) => {
        state.isFetchingGemsActivity = false;
      })
      .addCase(getMocktestAdmin.fulfilled, (state, action) => {
        if (action.payload.page_no === 1) {
          state.mocktests = action.payload;
        } else if (state.mocktests) {
          state.mocktests.mocktest = [
            ...(state.mocktests.mocktest ?? []),
            ...(action.payload.mocktest ?? []),
          ];
          state.mocktests.page_no = action.payload.page_no;
          state.mocktests.total_pages = action.payload.total_pages;
          state.mocktests.page_size = action.payload.page_size;
        }
      })
      .addCase(getReferralSummaryAdmin.fulfilled, (state, action) => {
        state.referralSummary = action.payload;
      })
      .addCase(getSkillStatisticsAdmin.fulfilled, (state, action) => {
        state.skillStatistics = action.payload;
      })
      .addCase(getReferralActivityLogsAdmin.fulfilled, (state, action) => {
        state.referralActivityLogs = action.payload;
      })

      .addCase(sendWhatsappNotification.fulfilled, (state, action) => {
        state.whatsappDryRun = action.payload;
        state.sendingWhatsapp = false;
      })
      .addCase(sendWhatsappNotification.pending, (state) => {
        state.sendingWhatsapp = true;
      })
      .addCase(getAdminMailTemplateById.fulfilled, (state, action) => {
        state.templateDetails = action.payload;
      })
      .addCase(getWhatsappTemplates.fulfilled, (state, action) => {
        state.whatsappTemplates = action.payload.templates;
      })
      .addCase(getAllLevels.fulfilled, (state, action) => {
        state.allLevels = action.payload;
      })
      .addCase(getAllBadge.fulfilled, (state, action) => {
        state.allBadges = action.payload;
      })
      .addCase(getAllCoupons.fulfilled, (state, action) => {
        state.allCoupons = action.payload;
      })
      .addCase(getCouponById.fulfilled, (state, action) => {
        state.couponById = action.payload;
      })
      .addCase(getBadgeType.fulfilled, (state, action) => {
        state.badgeTypes = action.payload;
      })
      .addCase(getLevelById.fulfilled, (state, action) => {
        state.levelById = action.payload;
      })
      .addCase(getAdminFeedbacks.fulfilled, (state, action) => {
        state.feedbacks = action.payload;
      })
      .addCase(getSkillTypeLists.fulfilled, (state, action) => {
        if (action.meta.arg?.skipStore) return;
        state.skillList = action.payload.data;
        state.adminSkillList = action.payload.data;
      })
      .addCase(updateSkillCategoriesAdmin.fulfilled, (state, action) => {
        // Patch the row in-place so the UI reflects the new categories
        // without re-fetching the whole list.
        const { skillId, categories } = action.payload;
        const row = state.adminSkillList?.domain_list?.find(
          (s: any) => s.id === skillId,
        );
        if (row) {
          (row as any).categories = categories;
        }
      })
      .addCase(updateSkillAllowImagesAdmin.fulfilled, (state, action) => {
        const { skillId, allowImages, effective } = action.payload;
        const row = state.adminSkillList?.domain_list?.find(
          (s: any) => s.id === skillId,
        );
        if (row) {
          (row as any).allow_images = allowImages;
          (row as any).effective_allow_images = effective;
        }
      })
      .addCase(updateSkillHiddenAdmin.fulfilled, (state, action) => {
        const { skillId, hidden } = action.payload;
        const list = state.adminSkillList?.domain_list;
        if (!list) return;
        const row = list.find((s: any) => s.id === skillId);
        if (row) {
          (row as any).is_hidden_by_default = hidden;
          (row as any).effective_is_hidden_by_default = hidden;
        }
        // If a Domain row toggles, child Modules currently in the list
        // need their effective flag refreshed too — admin sees the
        // cascade without reloading.
        if (row && (row as any).skill_type === 'Domain') {
          list.forEach((s: any) => {
            if (s.domain_id === skillId || s.parent_skill_id === skillId) {
              s.effective_is_hidden_by_default =
                hidden || !!s.is_hidden_by_default;
            }
          });
        }
      })
      .addCase(recomputeSkillQualityAdmin.fulfilled, (state, action) => {
        // Patch the affected row(s) so the UI reflects the freshly-computed
        // quality without re-fetching the whole list.
        const payload: any = action.payload ?? {};
        const mods: any[] = payload.modules
          ? payload.modules
          : payload.module
            ? [payload.module]
            : [];
        if (!mods.length) return;
        const list = state.adminSkillList?.domain_list;
        if (!list) return;
        mods.forEach((m) => {
          const row = list.find((s: any) => s.id === m.module_id);
          if (!row) return;
          (row as any).quality_incorrect_pct = m.incorrect_pct ?? null;
          (row as any).quality_duplicate_pct = m.duplicate_pct ?? null;
          (row as any).quality_image_pct = m.image_quality_pct ?? null;
          (row as any).quality_image_count = m.image_question_count ?? 0;
          // Hold the raw counts so the inline display can explain *why*
          // quality is still pending (not-eligible vs awaiting validation).
          (row as any).quality_diagnostics = m;
          if (m.incorrect_pct != null || m.image_quality_pct != null) {
            (row as any).quality_last_computed_at = new Date().toISOString();
          }
        });
      })
      .addCase(getMatchingProfile.fulfilled, (state, action) => {
        if (action.payload.page_no === 1) {
          state.matchingUsers = action.payload;
        } else if (state.matchingUsers) {
          state.matchingUsers.matching_profiles = [
            ...(state.matchingUsers.matching_profiles ?? []),
            ...(action.payload.matching_profiles ?? []),
          ];
          state.matchingUsers.page_no = action.payload.page_no;
          state.matchingUsers.page_size = action.payload.page_size;
          state.matchingUsers.total_pages = action.payload.total_pages;
          state.matchingUsers.matching_profiles_count =
            action.payload.matching_profiles_count;
        }
      })
      .addCase(getMatchingResumes.fulfilled, (state, action) => {
        if (action.payload.page_no === 1) {
          state.matchingResumes = action.payload;
        } else if (state.matchingResumes) {
          state.matchingResumes.matching_profiles = [
            ...(state.matchingResumes.matching_profiles ?? []),
            ...(action.payload.matching_profiles ?? []),
          ];
          state.matchingResumes.page_no = action.payload.page_no;
          state.matchingResumes.page_size = action.payload.page_size;
          state.matchingResumes.total_pages = action.payload.total_pages;
          state.matchingResumes.matching_profiles_count =
            action.payload.matching_profiles_count;
        }
      })
      .addCase(getOrgActivityLogs.fulfilled, (state, action) => {
        state.activityLogs = action.payload.activity_logs;
      })
      .addCase(getUserMailLogs.fulfilled, (state, action) => {
        state.mailLogs = action.payload.mail_logs;
      })
      .addCase(getOrgsDetailsById.fulfilled, (state, action) => {
        const [firstOrg] = action.payload.org_list || [];

        if (firstOrg) {
          state.orgDetails = firstOrg;
        }

        state.isFetchingOrg = false;
      })
      .addCase(getReviewOrgs.rejected, (state) => {
        state.isFetchingOrg = false;
      });

    // admin chat templates
    builder
      .addCase(getAllAdminChatTemplates.fulfilled, (state, action) => {
        if (action.payload.page === 1) {
          state.adminChatTemplates = action.payload;
        } else if (state.adminChatTemplates?.all_chat_templates) {
          state.adminChatTemplates.all_chat_templates =
            state.adminChatTemplates.all_chat_templates.concat(
              action.payload.all_chat_templates ?? [],
            );
          state.adminChatTemplates.page = action.payload.page;
          state.adminChatTemplates.page_size = action.payload.page_size;
          state.adminChatTemplates.total_pages = action.payload.total_pages;
        }
      })
      .addCase(deleteAdminChatTemplate.fulfilled, (state, action) => {
        if (state.adminChatTemplates?.all_chat_templates) {
          state.adminChatTemplates.all_chat_templates =
            state.adminChatTemplates.all_chat_templates.filter(
              (template) => template.id !== action.meta.arg.templateId,
            );
        }
      })
      .addCase(createAdminChatTemplate.fulfilled, (state, action) => {
        if (state.adminChatTemplates?.all_chat_templates) {
          state.adminChatTemplates.all_chat_templates.push(action.payload);
        }
      })
      .addCase(updateAdminChatTemplate.fulfilled, (state, action) => {
        if (state.adminChatTemplates?.all_chat_templates) {
          state.adminChatTemplates.all_chat_templates =
            state.adminChatTemplates.all_chat_templates.map((template) => {
              if (template.id === action.payload.id) {
                return action.payload;
              }
              return template;
            });
        }
      });

    builder.addCase(
      acceptJobReferralVerification.fulfilled,
      (state, action) => {
        if (state.feedbacks?.feedback_reports) {
          state.feedbacks.feedback_reports =
            state.feedbacks?.feedback_reports?.map((feedback) =>
              feedback.id === action.payload.data?.id
                ? { ...feedback, status: action.payload?.data?.status }
                : feedback,
            );
        }
      },
    );
  },
});
export const {
  resetReviewJobById,
  resetMocktestById,
  removeReviewedQuestionsFromList,
  updateAppliedStatus,
  setBreadcrumb,
  addBreadcrumb,
  resetBreadcrumb,
  trimBreadcrumb,
} = adminSlice.actions;

export default adminSlice.reducer;
